import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Transition } from '@headlessui/react';
import Storefront from 'utils/storefront';
import { formatProduct } from 'utils/storefront/format-resource';
import TrapFocus from 'utils/trap-focus';
import Close from 'utils/icon/icon-close.svg';
import clsx from 'clsx';
import { productByHandleGet } from '../utils/storefront/queries';
import QuickViewProduct from './quick-view-product';
import ProductContext from '../product/product-context';

const QuickView = () => {
  const storefront = Storefront.getInstance();
  const [productData, setProductData] = useState(null);
  const [isOpened, setIsOpened] = useState(false);

  const quickView = {
    open: (handle) => {
      const fetchData = async () => {
        setProductData(await getProductData(handle));
      };
      fetchData();
    },
  };

  const handleExternalQuickView = (e) => {
    quickView.open(e.detail.handle);
  };

  const handleClose = () => {
    setIsOpened(false);
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('quick-view')) {
      searchParams.delete('quick-view');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${searchParams.toString()}`,
      );
    }
  };

  const handleViewFromQuery = () => {
    const url = new URL(window.location.href);
    const handle = url.searchParams.get('quick-view');
    if (handle) {
      quickView.open(handle);
    }
  }

  useEffect(() => {
    document.addEventListener('quick-view:open', handleExternalQuickView);
    document.addEventListener('cart:add', handleClose);
    handleViewFromQuery();

    return () => {
      document.removeEventListener('quick-view:open', handleExternalQuickView);
      document.removeEventListener('cart:add', handleClose);
    };
  }, []);

  const getProductData = async (handle) => {
    const response = await storefront.request({
      query: productByHandleGet,
      variables: {
        handle,
      },
    });

    if (!response.data?.productByHandle) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', response);
      return null;
    }

    return {
      product: formatProduct(response?.data?.productByHandle, 'product'),
    };
  };

  useEffect(() => {
    const initQuickView = () => {
      setIsOpened(true);
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('quick-view:loaded', {
            detail: {
              id: productData.product.id,
            },
          }),
        );
      }, 100);
    };

    if (!productData) return;
    initQuickView();
  }, [productData]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpened) {
      if (!trapFocus) {
        trapFocus = new TrapFocus(
          document.querySelector('.quick-view__content'),
        );
        document.body.classList.add('overflow-hidden');
        trapFocus.init(true);
      }
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
      if (trapFocus)
        trapFocus.destroy(
          document.querySelector(
            `[data-handle='${productData.product.handle}']`,
          ),
        );
    };
  }, [isOpened, trapFocus]);

  return (
    <>
      <div
        className={clsx(
          'quick-shop quick-view quick-view__content fixed flex flex-col w-full top-0 bottom-0 right-0 bg-white z-[1003] md:w-[507px] ease-in duration-300',
          {
            'translate-x-0': isOpened,
            'translate-x-full transition-all': !isOpened,
          },
        )}
      >
        <button
          className="quick-view__button absolute w-24 h-24 top-28 right-24"
          aria-label={window.TMW.Strings.icons.close}
          title={window.TMW.Strings.icons.close}
          tabIndex="0"
          onClick={handleClose}
        >
          <Close />
        </button>
        {isOpened && (
          <ProductContext data={productData}>
            <QuickViewProduct />
          </ProductContext>
        )}
      </div>
      <Transition
        show={isOpened}
        as={Fragment}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black/20 z-50"
          aria-hidden="true"
          onClick={() => handleClose()}
        />
      </Transition>
    </>
  );
};

export default QuickView;
